import { Injectable } from '@angular/core';
import { combineQueries } from '@datorama/akita';
import { AclAssetPermission } from '@y42/acl/models';
import { AbstractFeatureQueryEntity, Y42FeatureToAclAssetResourceType } from '@y42/feature';
import { FeatureAclQuery } from '@y42/feature/acl';
import { Y42FeatureType } from '@y42/json-schema';
import { assertNonNullable } from '@y42/util/assert';
import { combineLatest, debounceTime, filter, map, Observable, shareReplay } from 'rxjs';
import {
  StatusAlertListViewData,
  StatusAlertState,
  StatusAlertStoreModel,
  StatusAlertUIModel,
} from './status-alert.model';
import { StatusAlertStore } from './status-alert.store';

@Injectable({ providedIn: 'root' })
export class StatusAlertQuery extends AbstractFeatureQueryEntity<StatusAlertState, StatusAlertUIModel> {
  constructor(protected store: StatusAlertStore, private readonly featureAclQuery: FeatureAclQuery) {
    super({
      store,
      featureType: Y42FeatureType.StatusAlert,
      toUIModel: (storeModel) => {
        const resourceType = Y42FeatureToAclAssetResourceType[Y42FeatureType.StatusAlert];
        assertNonNullable(resourceType);

        return {
          ...storeModel,
          featureType: Y42FeatureType.StatusAlert,
          last_used: this.getValue().last_used?.[storeModel.$path] || null,
          starred: !!this.getValue().starred[storeModel.$path],
          hasReadPermission: !!this.featureAclQuery.getPermission({
            resourceType,
            assetPath: storeModel.$path,
            permission: AclAssetPermission.Read,
          })?.allowed,
        };
      },
    });
  }

  readonly active$ = combineQueries([
    this.selectActive() as Observable<StatusAlertStoreModel | undefined>,
    this.select((st) => st.last_used),
    this.select((st) => st.starred),
  ]).pipe(
    debounceTime(0),
    map(([alert]) => {
      if (!alert) {
        return undefined;
      } else {
        return this.toUIModel(alert);
      }
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly items$: Observable<StatusAlertUIModel[]> = combineQueries([
    this.selectAll() as Observable<StatusAlertStoreModel[]>,
    this.select((st) => st.last_used),
    this.select((st) => st.starred),
  ]).pipe(
    debounceTime(0),
    map(([alerts]) => alerts.map((alert) => this.toUIModel(alert))),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly listItems$: Observable<StatusAlertListViewData[]> = combineLatest([this.loading$, this.items$]).pipe(
    filter(([loading]) => !loading),
    map(([_, alerts]) => alerts.map(this.toListViewModel)),
    map((arr) => arr.flat()),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly toListViewModel = (alert: StatusAlertUIModel): StatusAlertListViewData[] => {
    const baseRow: StatusAlertListViewData = {
      ...alert,
      id: alert.$path,
    };

    return [baseRow];
  };
}
